import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { getAuthDeviceContext } from './authDevice';
import { getAuthSessionState, clearAuthSession, syncAuthSessionFromPayload } from './authSessionStore';
import { markExpectedSessionExpiry } from './authSession';
import { getAuthEpoch, getAuthSignal, isLogoutPending, refreshOnce, syncChangedSession } from './browserAuthRuntime';

const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });
export const authClient = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true, timeout: 10000 });
const isPublic = (config) => config.publicRequest || ['/public', '/linkpage/public', '/linkpage/preview', '/promotions/public', '/stripe/public-product-catalog']
  .some((prefix) => config.url?.startsWith(prefix));
const isAuth = (config) => config.url?.startsWith('/auth/v2/');
const canceled = () => new axios.CanceledError('O contexto da sessão mudou');

function prepare(config) {
  config.headers = config.headers || {};
  delete config.headers.Authorization;
  delete config.headers.authorization;
  config._publicRequest = isPublic(config);
  if (!config._publicRequest) {
    if (isLogoutPending() && !config.url?.endsWith('/logout')) throw canceled();
    config._authEpoch = getAuthEpoch();
    const sessionSignal = getAuthSignal();
    if (config.signal && config.signal !== sessionSignal) {
      if (typeof AbortSignal.any === 'function') config.signal = AbortSignal.any([config.signal, sessionSignal]);
    } else config.signal = sessionSignal;
    if (!['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) config.headers['X-CSRF-Protection'] = '1';
    const context = getAuthSessionState().session?.auth?.contextId;
    if (context) config.headers['X-Session-Context'] = context;
  }
  return config;
}

api.interceptors.request.use(prepare);
authClient.interceptors.request.use(prepare);
function verifyResponse(response) {
  if (!response.config._publicRequest && response.config._authEpoch !== getAuthEpoch()) throw canceled();
  return response;
}
authClient.interceptors.response.use(verifyResponse);

api.interceptors.response.use(verifyResponse, async (error) => {
  const config = error.config || {};
  if (config._publicRequest || config._authEpoch === undefined) throw error;
  if (config._authEpoch !== getAuthEpoch()) throw canceled();
  const code = error.response?.data?.code;
  if (code === 'SESSION_CONTEXT_CHANGED' && !config._contextRetry) {
    await syncChangedSession();
    if ((config.method || 'get').toLowerCase() === 'get') return api({ ...config, signal: undefined, _contextRetry: true });
    throw error;
  }
  if (['AUTH_ACCESS_MISSING', 'AUTH_ACCESS_EXPIRED'].includes(code) && !config._retry
      && (!isAuth(config) || config.url.endsWith('/session'))) {
    try {
      await refreshOnce(async () => {
        const response = await authClient.post('/auth/v2/refresh', getAuthDeviceContext());
        if (!response.data?.session) throw new Error('Resposta de sessão inválida');
        syncAuthSessionFromPayload(response.data);
      });
      if (config._authEpoch !== getAuthEpoch() || isLogoutPending()) throw canceled();
      return api({ ...config, _retry: true });
    } catch (refreshError) {
      if (config._authEpoch === getAuthEpoch() && refreshError.response?.data?.code === 'AUTH_SESSION_INVALID') {
        clearAuthSession();
        if (code === 'AUTH_ACCESS_EXPIRED') {
          throw markExpectedSessionExpiry(refreshError) || refreshError;
        }
        throw refreshError;
      }
      throw refreshError;
    }
  }
  if (code === 'AUTH_SESSION_INVALID') {
    clearAuthSession();
    throw markExpectedSessionExpiry(error) || error;
  }
  throw error;
});

export default api;
